/**
 * OpenTelemetry Web SDK initialization for the Djayma storefront.
 *
 * - Traces all fetch calls automatically via auto-instrumentations-web.
 * - Injects X-Trace-Id on every API request so backend spans can be correlated.
 * - Exports to Sentry when VITE_SENTRY_DSN is configured (via @sentry/opentelemetry).
 * - Falls back to console exporter in development when Sentry DSN is absent.
 *
 * Call initOtelWeb() once at app startup (before createRoot).
 *
 * Note: @opentelemetry/sdk-trace-web and auto-instrumentations-web are heavy.
 * Only enabled when VITE_OTEL_ENABLED=true (default: disabled in dev, enabled in prod build).
 */

import { WebTracerProvider } from '@opentelemetry/sdk-trace-web';
import { Resource } from '@opentelemetry/resources';
import { SEMRESATTRS_SERVICE_NAME, SEMRESATTRS_SERVICE_VERSION, SEMRESATTRS_DEPLOYMENT_ENVIRONMENT } from '@opentelemetry/semantic-conventions';
import { BatchSpanProcessor, ConsoleSpanExporter, SimpleSpanProcessor } from '@opentelemetry/sdk-trace-base';
import { ZoneContextManager } from '@opentelemetry/context-zone';
import { registerInstrumentations } from '@opentelemetry/instrumentation';
import { FetchInstrumentation } from '@opentelemetry/instrumentation-fetch';
import { XMLHttpRequestInstrumentation } from '@opentelemetry/instrumentation-xml-http-request';
import type { SpanProcessor } from '@opentelemetry/sdk-trace-base';
import { generateTraceparent, TRACE_ID_RESPONSE_HEADER } from '@cieda/shared';

// ---------------------------------------------------------------------------
// Config
// ---------------------------------------------------------------------------

const SERVICE_NAME = 'djayma-web';
const SERVICE_VERSION = import.meta.env.VITE_APP_VERSION ?? '0.0.1';
const ENVIRONMENT = import.meta.env.MODE; // "production" | "development" | "staging"
const IS_PRODUCTION = ENVIRONMENT === 'production';
const OTEL_ENABLED = import.meta.env.VITE_OTEL_ENABLED === 'true';
const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN;
const API_URL = import.meta.env.VITE_API_URL ?? '';

// ---------------------------------------------------------------------------
// Exporter / processor
// ---------------------------------------------------------------------------

function buildProcessor(): SpanProcessor {
  if (IS_PRODUCTION && SENTRY_DSN) {
    // When Sentry is active its OpenTelemetry integration captures spans
    // automatically — we only need the BatchSpanProcessor to buffer them.
    // The actual export to Sentry happens via the Sentry SDK transport,
    // not through a separate OTel exporter.
    return new BatchSpanProcessor(new ConsoleSpanExporter(), {
      maxQueueSize: 128,
      scheduledDelayMillis: 5_000,
    });
  }
  // Development / no Sentry: log to console
  return new SimpleSpanProcessor(new ConsoleSpanExporter());
}

// ---------------------------------------------------------------------------
// Provider singleton
// ---------------------------------------------------------------------------

let _provider: WebTracerProvider | null = null;

/** Active traceparent for the current page load — used to inject into fetch calls. */
let _activeTraceparent: string = generateTraceparent();

export function getActiveTraceparent(): string {
  return _activeTraceparent;
}

export function refreshTraceparent(): string {
  _activeTraceparent = generateTraceparent();
  return _activeTraceparent;
}

export function initOtelWeb(): void {
  // In development, only enable OTel when explicitly opted in.
  // In production, always enable.
  if (!IS_PRODUCTION && !OTEL_ENABLED) {
    return;
  }

  if (_provider) return;

  _provider = new WebTracerProvider({
    resource: new Resource({
      [SEMRESATTRS_SERVICE_NAME]: SERVICE_NAME,
      [SEMRESATTRS_SERVICE_VERSION]: SERVICE_VERSION,
      [SEMRESATTRS_DEPLOYMENT_ENVIRONMENT]: ENVIRONMENT,
    }),
    spanProcessors: [buildProcessor()],
  });

  _provider.register({
    contextManager: new ZoneContextManager(),
  });

  // Auto-instrument fetch and XHR calls
  registerInstrumentations({
    instrumentations: [
      new FetchInstrumentation({
        // Propagate trace context to API calls only (avoid 3rd-party CORS issues)
        propagateTraceHeaderCorsUrls: [
          new RegExp(`^${API_URL.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}`),
        ],
        // Record what was fetched for debugging (never records body content)
        clearTimingResources: true,
      }),
      new XMLHttpRequestInstrumentation({
        propagateTraceHeaderCorsUrls: [
          new RegExp(`^${API_URL.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}`),
        ],
      }),
    ],
  });

  console.log(
    `[OTel] Web SDK initialized — service="${SERVICE_NAME}" env="${ENVIRONMENT}"`,
  );
}

// ---------------------------------------------------------------------------
// Fetch wrapper that injects X-Trace-Id header
// ---------------------------------------------------------------------------

/**
 * Wrap fetch to:
 * 1. Inject `traceparent` header on every API request (W3C trace context).
 * 2. Extract `X-Trace-Id` from the response and return it for logging.
 *
 * This is a convenience wrapper — the OTel FetchInstrumentation above also
 * adds traceparent automatically when the URL matches propagateTraceHeaderCorsUrls.
 * This wrapper is for calls made outside the instrumented context.
 */
export async function tracedApiFetch(
  input: RequestInfo | URL,
  init?: RequestInit,
): Promise<Response> {
  const headers = new Headers(init?.headers as HeadersInit | undefined);

  // Inject W3C trace context
  const traceparent = _activeTraceparent;
  headers.set('traceparent', traceparent);

  const response = await fetch(input, { ...init, headers });

  // Store the trace ID echoed back from the backend for RUM correlation
  const backendTraceId = response.headers.get(TRACE_ID_RESPONSE_HEADER);
  if (backendTraceId) {
    storeLastBackendTraceId(backendTraceId);
  }

  return response;
}

let _lastBackendTraceId: string | null = null;

function storeLastBackendTraceId(id: string): void {
  _lastBackendTraceId = id;
}

/** Returns the most recent X-Trace-Id echoed back from distribution-api. */
export function getLastBackendTraceId(): string | null {
  return _lastBackendTraceId;
}

